// Saving for the page editor: what counts as an unsaved change, draft
// autosave, publish / unpublish, and writes that never overlap.
//
// A page has one content column, so a live page's content goes live on every
// write. That is why only DRAFTS autosave: a live page keeps its edits in the
// editor until "Publish changes".
//
// Every write carries base_updated_at, the updated_at of the row this editor
// last loaded or wrote. The server answers 409 (code 'stale') when someone
// else (another tab, another person) wrote the page since: the saver then
// stops writing for good and reports it, and the editor offers a reload.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { syncLegacySections, type LandingPage, type PageEdits } from '../model/pages';
import { ApiError } from '../api';

export type { PageEdits };

/** save = write the edits (status unchanged). publish = write the edits as
 *  live. unpublish = take the page offline (status only; a draft then
 *  autosaves its edits). */
export type SaveMode = 'save' | 'publish' | 'unpublish';

/** Drafts save this long after the last edit. */
export const AUTOSAVE_MS = 1500;

export function pageEdits(p: LandingPage): PageEdits {
  return { name: p.name, slug: p.slug, form_id: p.form_id ?? null, content: syncLegacySections(p.content ?? {}), theme: p.theme ?? {} };
}

/** Identity of a page's edits: compared against the last written one to tell
 *  whether anything is unsaved. */
export function editKey(p: LandingPage): string {
  return JSON.stringify(pageEdits(p));
}

const FAILED: Record<SaveMode, string> = {
  save: 'Couldn’t save.',
  publish: 'Couldn’t publish.',
  unpublish: 'Couldn’t unpublish.',
};

/** What the header says when the page changed somewhere else. */
export const STALE_MESSAGE = 'Changed elsewhere.';

/**
 * failed: the write did not land (network, server); Retry tries again.
 * invalid: something on the page must change first (a name, a link the server
 *   refused, a deleted form); `field` names it when the server did.
 * stale: someone else wrote the page; nothing more is written until a reload.
 */
export type SaveErrorKind = 'failed' | 'invalid' | 'stale';
export interface SaveError {
  mode: SaveMode; message: string; retryable: boolean; kind: SaveErrorKind;
  /** The field the server named ('slug'). */
  field?: string;
  /** From validate(): clears by itself once the problem is fixed. */
  local?: boolean;
}

/** What a write answers: the page row as the server now has it. */
export type WrittenRow = Pick<LandingPage, 'updated_at'> & Partial<LandingPage> & { form_went_live?: boolean };

export interface PageSaverOptions {
  page: LandingPage | null;
  setPage: Dispatch<SetStateAction<LandingPage | null>>;
  /** Performs the write and answers the row as written. `base` is the
   *  updated_at this editor last saw (send it as base_updated_at). For
   *  'unpublish' only the status needs writing. */
  write: (id: string, edits: PageEdits, mode: SaveMode, base: string | null) => Promise<WrittenRow | null | undefined | void>;
  /** Draft autosave on/off (off for people who can only view). */
  autosave: boolean;
  /** Why these edits can't be written right now, else null (e.g. a changed
   *  page link that is invalid or taken). Gets the last saved edits to
   *  compare with (null before the first load settles). Blocks save and
   *  publish, never unpublish. */
  validate?: (page: LandingPage, saved: PageEdits | null) => string | null;
  /** After each successful write, with the page exactly as written, and the
   *  row the server answered (null when the write answered nothing). */
  onSaved?: (written: LandingPage, mode: SaveMode, row: WrittenRow | null) => void;
  /** The page changed somewhere else (409 stale). Called once. */
  onStale?: () => void;
}

export interface PageSaver {
  /** Edits not written yet. */
  dirty: boolean;
  /** validate()'s answer for the current edits. */
  blocked: string | null;
  /** The last written edits of this page. */
  saved: PageEdits | null;
  /** The write in progress, if any. */
  running: SaveMode | null;
  /** The last failure, until the next successful write (a stale one stays). */
  error: SaveError | null;
  /** The page changed somewhere else: nothing more is written. */
  stale: boolean;
  /** Write now. Waits for a write already in flight, then writes the LATEST
   *  state. Resolves true when it landed. */
  run: (mode: SaveMode) => Promise<boolean>;
  /** Re-run the write that failed. */
  retry: () => void;
  /** Same as `dirty`, read from refs: safe right after an await. */
  isDirty: () => boolean;
  /** Resolves once no write is in flight. */
  whenIdle: () => Promise<void>;
  /** A field was written outside the saver: fold it into the saved baseline
   *  so it doesn't read as an unsaved change. */
  acceptWritten: (patch: Partial<PageEdits>, updatedAt?: string) => void;
}

interface Baseline { id: string; edits: PageEdits; key: string }

/** Turns a failed write into what the header shows. */
export function saveErrorFor(mode: SaveMode, e: unknown): SaveError {
  if (e instanceof ApiError) {
    const data = (e.data ?? {}) as { code?: unknown; field?: unknown };
    if (e.status === 409 && data.code === 'stale') return { mode, message: STALE_MESSAGE, retryable: false, kind: 'stale' };
    const field = typeof data.field === 'string' ? data.field : undefined;
    if (e.status === 403) return { mode, message: 'You can view this page but not change it.', retryable: false, kind: 'invalid' };
    if ((e.status === 400 || e.status === 409 || e.status === 422) && e.message) return { mode, message: e.message, retryable: false, kind: 'invalid', field };
  }
  return { mode, message: FAILED[mode], retryable: true, kind: 'failed' };
}

export function usePageSaver({ page, setPage, write, autosave, validate, onSaved, onStale }: PageSaverOptions): PageSaver {
  const [saved, setSaved] = useState<Baseline | null>(null);
  const savedEdits = page && saved?.id === page.id ? saved.edits : null;
  const blocked = page && validate ? validate(page, savedEdits) : null;
  const [running, setRunning] = useState<SaveMode | null>(null);
  const [error, setError] = useState<SaveError | null>(null);
  const [stale, setStale] = useState(false);

  // Refs so async work (and the unmount flush) always sees the latest values.
  const pageRef = useRef(page);
  const savedRef = useRef(saved);
  const blockedRef = useRef(blocked);
  const autosaveRef = useRef(autosave);
  const writeRef = useRef(write);
  const onSavedRef = useRef(onSaved);
  const onStaleRef = useRef(onStale);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const inFlightOf = useRef<{ mode: SaveMode; key: string } | null>(null); // what the write in flight is writing
  const failedKey = useRef<string | null>(null); // edits that failed to write: no autosave loop on them
  const baseRef = useRef<string | null>(null);   // updated_at of the row this editor last saw
  const staleRef = useRef<SaveError | null>(null);
  useLayoutEffect(() => {
    pageRef.current = page; blockedRef.current = blocked; autosaveRef.current = autosave;
    writeRef.current = write; onSavedRef.current = onSaved; onStaleRef.current = onStale;
  });

  const setBaseline = useCallback((b: Baseline) => { savedRef.current = b; setSaved(b); }, []);

  // The page as first loaded counts as saved, and its updated_at is the base.
  useEffect(() => {
    if (page && savedRef.current?.id !== page.id) {
      const edits = pageEdits(page);
      setBaseline({ id: page.id, edits, key: JSON.stringify(edits) });
      baseRef.current = page.updated_at || null;
    }
  }, [page, setBaseline]);

  const key = useMemo(() => (page ? editKey(page) : null), [page]);
  const dirty = !!page && saved?.id === page.id && key !== saved.key;

  const isDirty = useCallback(() => {
    const p = pageRef.current;
    const s = savedRef.current;
    return !!p && s?.id === p.id && editKey(p) !== s.key;
  }, []);

  const run = useCallback(async (mode: SaveMode): Promise<boolean> => {
    // The same request again (a double ⌘S, a key event that arrives twice)
    // while that exact write is in flight just shares it.
    const now = pageRef.current;
    if (now && inFlight.current && inFlightOf.current?.mode === mode && inFlightOf.current.key === editKey(now)) return inFlight.current;
    // One write at a time, in order. Edits made while one is in flight are
    // written by the next run, never reverted by the one finishing.
    while (inFlight.current) await inFlight.current;
    const cur = pageRef.current;
    if (!cur) return false;
    if (staleRef.current) { setError({ ...staleRef.current, mode }); return false; }
    const why = mode === 'unpublish' ? null : blockedRef.current;
    if (why) { setError({ mode, message: why, retryable: false, kind: 'invalid', local: true }); return false; }
    const edits = pageEdits(cur);
    const key = JSON.stringify(edits);
    const job = (async () => {
      setRunning(mode);
      try {
        const row = (await writeRef.current(cur.id, edits, mode, baseRef.current)) || null;
        if (row && typeof row.updated_at === 'string' && row.updated_at) baseRef.current = row.updated_at;
        const status: LandingPage['status'] = mode === 'publish' ? 'published' : mode === 'unpublish' ? 'draft' : cur.status;
        if (pageRef.current?.id === cur.id) {
          if (mode !== 'unpublish') setBaseline({ id: cur.id, edits, key });
          if (status !== cur.status) setPage((p) => (p && p.id === cur.id ? { ...p, status } : p));
          failedKey.current = null;
          setError(null);
        }
        onSavedRef.current?.({ ...cur, ...edits, status }, mode, row);
        return true;
      } catch (e) {
        const err = saveErrorFor(mode, e);
        failedKey.current = key;
        if (err.kind === 'stale' && !staleRef.current) {
          staleRef.current = err;
          setStale(true);
          onStaleRef.current?.();
        }
        setError(err);
        return false;
      } finally {
        setRunning(null);
      }
    })();
    inFlight.current = job;
    inFlightOf.current = { mode, key };
    try { return await job; } finally { if (inFlight.current === job) { inFlight.current = null; inFlightOf.current = null; } }
  }, [setPage, setBaseline]);

  const retry = useCallback(() => { if (error?.retryable) void run(error.mode); }, [error, run]);

  const whenIdle = useCallback(async () => { while (inFlight.current) await inFlight.current; }, []);

  // A "fix this first" message clears as soon as the problem is fixed (never
  // a stale one: that needs a reload).
  useEffect(() => {
    if (!blocked) setError((e) => (e?.local ? null : e));
  }, [blocked]);

  const acceptWritten = useCallback((patch: Partial<PageEdits>, updatedAt?: string) => {
    if (updatedAt) baseRef.current = updatedAt;
    const s = savedRef.current;
    if (!s) return;
    const edits = { ...s.edits, ...patch };
    setBaseline({ id: s.id, edits, key: JSON.stringify(edits) });
  }, [setBaseline]);

  // Draft autosave: AUTOSAVE_MS after the last edit, never while a write is in
  // flight, and never again on the exact edits that just failed (a new edit
  // or Retry tries again). Never after the page went stale.
  const status = page?.status;
  useEffect(() => {
    if (!autosave || stale || status !== 'draft' || !dirty || running || blocked) return;
    if (failedKey.current !== null && failedKey.current === key) return;
    const t = setTimeout(() => { void run('save'); }, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [autosave, stale, status, dirty, running, blocked, key, error, run]);

  // Leaving the editor by any route (browser back, a link, a page switch):
  // write pending draft edits instead of dropping them with the timer.
  useEffect(() => () => {
    const p = pageRef.current;
    if (autosaveRef.current && !staleRef.current && p?.status === 'draft' && !blockedRef.current && isDirty()) void run('save');
  }, [isDirty, run]);

  return { dirty, blocked, saved: savedEdits, running, error, stale, run, retry, isDirty, whenIdle, acceptWritten };
}
